/**
 * PWA helpers — install prompt + online/offline state + offline-queue sync.
 *
 * These are tiny React hooks rather than a full provider since they
 * each touch a single browser API.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { flushQueue, pendingCount, subscribeQueue } from './offlineQueue';

/**
 * The browser-fired `beforeinstallprompt` event isn't in lib.dom yet.
 * We type it loosely here so we can stash it and call `.prompt()` later.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function useInstallPrompt() {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(() =>
    typeof window !== 'undefined' &&
    window.matchMedia('(display-mode: standalone)').matches,
  );

  useEffect(() => {
    function onBeforeInstall(e: Event) {
      // Prevent the default Chrome mini-infobar
      e.preventDefault();
      setPromptEvent(e as BeforeInstallPromptEvent);
    }
    function onAppInstalled() {
      setInstalled(true);
      setPromptEvent(null);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  async function prompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!promptEvent) return 'unavailable';
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    setPromptEvent(null);
    return choice.outcome;
  }

  return {
    canInstall: !!promptEvent && !installed,
    installed,
    prompt,
  };
}

export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/**
 * Live count of offline-queued mutations + automatic replay (B.1).
 * Mount once in the shell: flushes on app start (a queued tap may have
 * survived a reload) and whenever connectivity returns, reports the result,
 * then refetches everything the synced mutations touched.
 */
export function useOfflineQueue(): { pending: number } {
  const [pending, setPending] = useState(0);
  const qc = useQueryClient();

  useEffect(() => {
    let alive = true;
    const refreshCount = () => {
      void pendingCount().then((n) => {
        if (alive) setPending(n);
      });
    };
    refreshCount();
    const unsubscribe = subscribeQueue(refreshCount);

    async function sync() {
      const result = await flushQueue();
      if (result.synced > 0) {
        toast.success(
          `Synced ${result.synced} update${result.synced === 1 ? '' : 's'} captured offline`,
        );
        // Server truth changed — refetch the views the queued writes touched.
        void qc.invalidateQueries();
      }
      for (const r of result.rejected) {
        toast.error(`Couldn't sync "${r.label}": ${r.message}`);
      }
      if (result.rejected.length > 0) void qc.invalidateQueries();
    }

    if (navigator.onLine) void sync();
    const onOnline = () => void sync();
    window.addEventListener('online', onOnline);
    return () => {
      alive = false;
      unsubscribe();
      window.removeEventListener('online', onOnline);
    };
  }, [qc]);

  return { pending };
}
